/**
 * Provisioning-setup shell at /provisioning-setup/:cognitoryId.
 * Verifies library_setup access, then library-state + optional corpus bootstrap.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useCognitoryAuth } from "../auth/useCognitoryAuth";
import {
  useProvisioningSetupAccess,
  type ProvisioningSetupAccessPhase,
} from "../entry/useProvisioningSetupAccess";
import {
  useProvisioningSetupWorkflow,
  type ProvisioningSetupActivationTerminalDecision,
  type ProvisioningSetupActivationTerminalResult,
  type ProvisioningSetupLibraryStatePhase,
} from "../entry/useProvisioningSetupWorkflow";
import type { ProvisioningLibraryStateSetupPhase } from "../api/cognitoryProvisioningLibraryStateClient";
import { parseProvisioningSetupRouteCognitoryId } from "../presentation/cognitoryProvisioningSetupNavigation";
import { useParams } from "react-router-dom";
import FusionSun from "./FusionSun";
import CognitoryProvisioningSetupLibraryPanel from "./CognitoryProvisioningSetupLibraryPanel";
import {
  MAIN_PAGE_BODY_STYLE,
  MAIN_PAGE_COLORS,
  MAIN_PAGE_CONTENT_Z_INDEX,
  MAIN_PAGE_FONT_STACK,
  MAIN_PAGE_LIQUID_BUTTON_STYLE,
  MAIN_PAGE_PANEL_INNER_STYLE,
  MAIN_PAGE_PANEL_STYLE,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
  MAIN_PAGE_SECTION_LABEL_STYLE,
  mainPageLiquidButtonHandlers,
} from "../theme/mainPageVisual";

function phaseStatusLabel(phase: ProvisioningSetupAccessPhase): string {
  switch (phase) {
    case "resolving":
      return "Verifying provisioning setup access…";
    case "authorized":
      return "Provisioning setup access verified.";
    case "forbidden":
      return "You do not have provisioning setup access for this Cognitory.";
    case "invalid_request":
      return "This provisioning setup link is invalid.";
    case "unauthenticated":
      return "Sign in to continue provisioning setup.";
    case "temporary_failure":
      return "Unable to verify provisioning setup access right now.";
    case "unable_to_verify":
      return "Unable to verify provisioning setup access.";
    case "idle_signed_out":
      return "Sign in to continue provisioning setup.";
    default:
      return "Checking provisioning setup access…";
  }
}

function setupPhaseLabel(phase: ProvisioningLibraryStateSetupPhase): string {
  switch (phase) {
    case "bootstrap_required":
      return "Corpus bootstrap required";
    case "library_initialized_draft":
      return "Library initialized (draft)";
    case "library_processing":
      return "Library processing";
    case "library_activation_ready":
      return "Library activation ready";
    default:
      return "Unknown setup phase";
  }
}

function resolveActivationTerminalDecisionForRoute(input: {
  requestedCognitoryId: string | null;
  persisted: {
    cognitoryId: string;
    decision: ProvisioningSetupActivationTerminalDecision;
  } | null;
  workflowTerminal: ProvisioningSetupActivationTerminalResult | null;
}): ProvisioningSetupActivationTerminalDecision | null {
  if (!input.requestedCognitoryId) {
    return null;
  }
  if (input.persisted?.cognitoryId === input.requestedCognitoryId) {
    return input.persisted.decision;
  }
  if (input.workflowTerminal?.cognitoryId === input.requestedCognitoryId) {
    return input.workflowTerminal.decision;
  }
  return null;
}

function activationTerminalSuccessMessage(
  decision: ProvisioningSetupActivationTerminalDecision,
): string {
  if (decision === "activation_already_exact") {
    return "This Cognitory is already active. You can open it from the main Cognitory list when it appears there.";
  }
  return "Activation completed. This Cognitory is now active. You can open it from the main Cognitory list when it appears there.";
}

function nextStepMessage(input: {
  setupPhase: ProvisioningLibraryStateSetupPhase | null;
  bootstrapAccepted: boolean;
  bootstrapRefreshFailed: boolean;
  uploadEligible: boolean;
  activationReviewVisible: boolean;
}): string {
  if (input.bootstrapRefreshFailed) {
    return "Corpus setup was accepted, but the current library state could not be verified. Use Refresh state to try again.";
  }
  if (input.activationReviewVisible) {
    return "Review activation readiness below. Activation makes this Cognitory enterable for owners.";
  }
  if (input.setupPhase === "library_activation_ready") {
    return "The document library is ready for the activation step.";
  }
  if (input.uploadEligible) {
    return "Upload a governed PDF to build this library.";
  }
  if (input.bootstrapAccepted) {
    return "Corpus scaffold setup is complete. Refresh state if upload controls do not appear.";
  }
  return "Initialize the corpus scaffold to prepare this Cognitory. This step does not upload documents or activate the Cognitory.";
}

function libraryStateLoadErrorMessage(
  phase: Exclude<
    ProvisioningSetupLibraryStatePhase,
    "idle" | "loading" | "resolved"
  >,
): string {
  switch (phase) {
    case "library_state_conflict":
      return "Library state could not be loaded because setup is out of date. Use Refresh state to try again.";
    case "unauthenticated":
      return "Sign in to load library state.";
    case "forbidden":
      return "You do not have access to load library state for this Cognitory.";
    case "invalid_request":
      return "This provisioning setup request is invalid.";
    case "system_not_eligible":
      return "This Cognitory is not eligible for provisioning setup right now.";
    case "temporary_failure":
      return "Unable to load library state right now. Try again later.";
    case "unable_to_verify":
      return "Unable to verify library state right now. Try again later.";
    default:
      return "Unable to load library state right now. Try again later.";
  }
}

function ProvisioningSetupPageShell(input: { children: ReactNode }) {
  return (
    <div
      data-cognitory-visual-shell="main-page"
      data-testid="cognitory-provisioning-setup-page"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        isolation: "isolate",
        background: MAIN_PAGE_COLORS.pageBackground,
        fontFamily: MAIN_PAGE_FONT_STACK,
      }}
    >
      <FusionSun />
      <div
        style={{
          position: "relative",
          zIndex: MAIN_PAGE_CONTENT_Z_INDEX,
          flex: "1 0 auto",
          width: "100%",
          maxWidth: "720px",
          margin: "0 auto",
          padding:
            "clamp(1.75rem, 4vw, 3rem) clamp(1.25rem, 4vw, 2rem) clamp(2rem, 4vw, 3rem)",
          boxSizing: "border-box",
        }}
      >
        <header style={{ textAlign: "center", marginBottom: "1.25rem" }}>
          <div style={MAIN_PAGE_SECTION_LABEL_STYLE}>PROVISIONING SETUP</div>
          <h1
            data-testid="cognitory-provisioning-setup-title"
            style={{
              margin: "0.5rem auto 0",
              fontWeight: 300,
              letterSpacing: "-0.03em",
              fontSize: "clamp(1.75rem, 3.6vw, 2.35rem)",
              color: MAIN_PAGE_COLORS.ink,
            }}
          >
            Cognitory setup
          </h1>
        </header>
        <div style={MAIN_PAGE_PANEL_STYLE}>
          <div style={MAIN_PAGE_PANEL_INNER_STYLE}>{input.children}</div>
        </div>
      </div>
    </div>
  );
}

export default function CognitoryProvisioningSetupPage(): JSX.Element {
  const params = useParams();
  const requestedCognitoryId = parseProvisioningSetupRouteCognitoryId(
    params.cognitoryId,
  );
  const auth = useCognitoryAuth();
  const signedIn = auth.status === "signed_in";
  const access = useProvisioningSetupAccess({
    signedIn,
    requestedCognitoryId,
  });
  const workflow = useProvisioningSetupWorkflow({
    enabled: access.phase === "authorized" && access.cognitory != null,
    cognitoryId: access.cognitory?.cognitoryId ?? null,
  });
  const [confirmBootstrap, setConfirmBootstrap] = useState(false);
  const [confirmActivation, setConfirmActivation] = useState(false);
  const [activationTerminalPersisted, setActivationTerminalPersisted] =
    useState<{
      cognitoryId: string;
      decision: ProvisioningSetupActivationTerminalDecision;
    } | null>(null);

  useEffect(() => {
    setActivationTerminalPersisted(null);
    setConfirmActivation(false);
  }, [requestedCognitoryId]);

  useEffect(() => {
    const terminal = workflow.activationTerminal;
    if (
      !terminal ||
      !requestedCognitoryId ||
      terminal.cognitoryId !== requestedCognitoryId
    ) {
      return;
    }
    setActivationTerminalPersisted({
      cognitoryId: terminal.cognitoryId,
      decision: terminal.decision,
    });
    setConfirmActivation(false);
  }, [workflow.activationTerminal, requestedCognitoryId]);

  const activationTerminalForRoute = resolveActivationTerminalDecisionForRoute({
    requestedCognitoryId,
    persisted: activationTerminalPersisted,
    workflowTerminal: workflow.activationTerminal,
  });

  if (activationTerminalForRoute) {
    return (
      <ProvisioningSetupPageShell>
        <div
          data-testid="cognitory-provisioning-setup-activation-terminal"
          data-cognitory-id={requestedCognitoryId}
          style={MAIN_PAGE_BODY_STYLE}
        >
          <p
            data-testid="cognitory-provisioning-setup-status"
            style={{ textAlign: "center", marginBottom: "0.85rem" }}
          >
            {activationTerminalSuccessMessage(activationTerminalForRoute)}
          </p>
        </div>
      </ProvisioningSetupPageShell>
    );
  }

  if (auth.status === "loading") {
    return (
      <ProvisioningSetupPageShell>
        <div
          data-testid="cognitory-provisioning-setup-loading-auth"
          style={MAIN_PAGE_BODY_STYLE}
        >
          <p style={{ textAlign: "center", marginBottom: 0 }}>
            Checking sign-in…
          </p>
        </div>
      </ProvisioningSetupPageShell>
    );
  }

  if (!signedIn) {
    return (
      <ProvisioningSetupPageShell>
        <div
          data-testid="cognitory-provisioning-setup-signed-out"
          style={MAIN_PAGE_BODY_STYLE}
        >
          <p
            data-testid="cognitory-provisioning-setup-status"
            style={{ textAlign: "center", marginBottom: "0.85rem" }}
          >
            {phaseStatusLabel("idle_signed_out")}
          </p>
          <div style={{ textAlign: "center" }}>
            <button
              type="button"
              data-testid="cognitory-provisioning-setup-sign-in"
              style={MAIN_PAGE_LIQUID_BUTTON_STYLE}
              {...mainPageLiquidButtonHandlers}
              onClick={() => {
                void auth.signIn();
              }}
            >
              Sign in with Google
            </button>
          </div>
        </div>
      </ProvisioningSetupPageShell>
    );
  }

  if (access.phase === "authorized" && access.cognitory) {
    const showBootstrapPanel =
      workflow.bootstrapUiPhase !== "hidden" &&
      workflow.setupPhase === "bootstrap_required";
    const bootstrapUiPhase = workflow.bootstrapUiPhase;
    const bootstrapPlan = workflow.bootstrapPlan;
    const showBootstrapConfirmation =
      bootstrapPlan != null &&
      (bootstrapUiPhase === "review" || bootstrapUiPhase === "executing");
    const isBootstrapExecuting = bootstrapUiPhase === "executing";
    const showActivationPanel =
      workflow.setupPhase === "library_activation_ready" &&
      workflow.activationTerminal == null;
    const activationReadinessPhase = workflow.activationReadinessPhase;
    const canSubmitActivation =
      confirmActivation &&
      activationReadinessPhase === "ready" &&
      !workflow.activationBusy;

    return (
      <ProvisioningSetupPageShell>
        <div
          data-testid="cognitory-provisioning-setup-shell"
          data-cognitory-id={access.cognitory.cognitoryId}
          style={MAIN_PAGE_BODY_STYLE}
        >
          <p
            data-testid="cognitory-provisioning-setup-status"
            style={{ textAlign: "center", marginBottom: "0.85rem" }}
          >
            {phaseStatusLabel("authorized")}
          </p>
          <p
            data-testid="cognitory-provisioning-setup-active-name"
            style={{
              textAlign: "center",
              fontSize: "clamp(1.15rem, 2.2vw, 1.45rem)",
              color: MAIN_PAGE_COLORS.inkStrong,
              marginBottom: "0.75rem",
            }}
          >
            {access.cognitory.displayName}
          </p>

          <div
            data-testid="cognitory-provisioning-setup-library-state"
            style={{ marginBottom: "1rem" }}
          >
            {workflow.libraryStatePhase === "loading" ? (
              <p
                data-testid="cognitory-provisioning-setup-library-state-loading"
                style={{ textAlign: "center", color: MAIN_PAGE_COLORS.soft }}
              >
                Loading library state…
              </p>
            ) : null}
            {workflow.libraryStatePhase === "resolved" &&
            workflow.setupPhase ? (
              <p
                data-testid="cognitory-provisioning-setup-library-state-phase"
                style={{ textAlign: "center", color: MAIN_PAGE_COLORS.slate }}
              >
                {setupPhaseLabel(workflow.setupPhase)}
              </p>
            ) : null}
            {workflow.libraryStatePhase !== "idle" &&
            workflow.libraryStatePhase !== "loading" &&
            workflow.libraryStatePhase !== "resolved" ? (
              <p
                data-testid="cognitory-provisioning-setup-library-state-error"
                role="alert"
                style={{
                  textAlign: "center",
                  color: MAIN_PAGE_COLORS.alertInk,
                }}
              >
                {libraryStateLoadErrorMessage(workflow.libraryStatePhase)}
              </p>
            ) : null}
            {workflow.libraryStateRequestId ? (
              <p
                data-testid="cognitory-provisioning-setup-library-state-request-id"
                style={{
                  textAlign: "center",
                  fontSize: "0.8rem",
                  color: MAIN_PAGE_COLORS.whisper,
                }}
              >
                Request ID: {workflow.libraryStateRequestId}
              </p>
            ) : null}
            <div style={{ textAlign: "center", marginTop: "0.65rem" }}>
              <button
                type="button"
                data-testid="cognitory-provisioning-setup-refresh-state"
                style={MAIN_PAGE_QUIET_BUTTON_STYLE}
                onClick={workflow.refreshLibraryState}
              >
                Refresh state
              </button>
            </div>
          </div>

          {showBootstrapPanel ? (
            <div
              data-testid="cognitory-provisioning-setup-bootstrap-panel"
              style={{ marginBottom: "1rem" }}
            >
              {showBootstrapConfirmation ? (
                <div data-testid="cognitory-provisioning-setup-bootstrap-review">
                  <ul
                    style={{
                      fontSize: "0.9rem",
                      color: MAIN_PAGE_COLORS.slate,
                      margin: "0.75rem auto",
                      paddingLeft: "1.25rem",
                      maxWidth: "28rem",
                      lineHeight: 1.55,
                    }}
                  >
                    <li>
                      A bounded corpus scaffold will be created for this
                      Cognitory.
                    </li>
                    <li>This operation does not upload any documents.</li>
                    <li>This operation does not activate the Cognitory.</li>
                    <li>
                      You must confirm below before the scaffold is created.
                    </li>
                  </ul>
                  {isBootstrapExecuting ? (
                    <p
                      data-testid="cognitory-provisioning-setup-bootstrap-executing"
                      style={{
                        textAlign: "center",
                        color: MAIN_PAGE_COLORS.soft,
                        margin: "0.5rem 0 0.75rem",
                      }}
                    >
                      Running corpus scaffold setup…
                    </p>
                  ) : null}
                  <label
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      alignItems: "flex-start",
                      fontSize: "0.9rem",
                      color: MAIN_PAGE_COLORS.slate,
                      justifyContent: "center",
                      opacity: isBootstrapExecuting ? 0.65 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      data-testid="cognitory-provisioning-setup-bootstrap-confirm"
                      checked={confirmBootstrap}
                      disabled={isBootstrapExecuting}
                      onChange={e => setConfirmBootstrap(e.target.checked)}
                    />
                    <span>
                      I confirm this creates the bounded corpus scaffold only.
                      It does not upload documents or activate the Cognitory.
                    </span>
                  </label>
                </div>
              ) : (
                <p
                  style={{ textAlign: "center", color: MAIN_PAGE_COLORS.soft }}
                >
                  Create the corpus scaffold for this Cognitory.
                </p>
              )}
              {workflow.bootstrapErrorMessage ? (
                <p
                  data-testid="cognitory-provisioning-setup-bootstrap-error"
                  role="alert"
                  style={{
                    textAlign: "center",
                    color: MAIN_PAGE_COLORS.alertInk,
                  }}
                >
                  {workflow.bootstrapErrorMessage}
                </p>
              ) : null}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.65rem",
                  justifyContent: "center",
                  marginTop: "0.75rem",
                }}
              >
                {showBootstrapConfirmation ? (
                  <button
                    type="button"
                    data-testid="cognitory-provisioning-setup-bootstrap-execute"
                    disabled={!confirmBootstrap || isBootstrapExecuting}
                    style={{
                      ...MAIN_PAGE_LIQUID_BUTTON_STYLE,
                      marginTop: 0,
                      opacity:
                        !confirmBootstrap || isBootstrapExecuting ? 0.65 : 1,
                    }}
                    {...mainPageLiquidButtonHandlers}
                    onClick={() => {
                      if (isBootstrapExecuting) {
                        return;
                      }
                      void workflow.executeBootstrap();
                    }}
                  >
                    {isBootstrapExecuting
                      ? "Running bootstrap…"
                      : "Run bootstrap"}
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="cognitory-provisioning-setup-bootstrap-plan"
                    disabled={bootstrapUiPhase === "planning"}
                    style={{
                      ...MAIN_PAGE_LIQUID_BUTTON_STYLE,
                      marginTop: 0,
                      opacity: bootstrapUiPhase === "planning" ? 0.65 : 1,
                    }}
                    {...mainPageLiquidButtonHandlers}
                    onClick={() => {
                      void workflow.startBootstrapPlan();
                    }}
                  >
                    {bootstrapUiPhase === "planning"
                      ? "Preparing plan…"
                      : "Review bootstrap plan"}
                  </button>
                )}
                {bootstrapUiPhase === "review" ? (
                  <button
                    type="button"
                    data-testid="cognitory-provisioning-setup-bootstrap-cancel"
                    style={MAIN_PAGE_QUIET_BUTTON_STYLE}
                    onClick={() => {
                      setConfirmBootstrap(false);
                      workflow.cancelBootstrapReview();
                    }}
                  >
                    Cancel
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {workflow.bootstrapAccepted ? (
            <p
              data-testid="cognitory-provisioning-setup-bootstrap-accepted"
              style={{
                textAlign: "center",
                color: MAIN_PAGE_COLORS.soft,
                marginBottom: "0.75rem",
              }}
            >
              Corpus scaffold setup is complete.
            </p>
          ) : null}

          {showActivationPanel ? (
            <div
              data-testid="cognitory-provisioning-setup-activation-panel"
              style={{ marginBottom: "1rem" }}
            >
              {activationReadinessPhase === "loading" ? (
                <p
                  data-testid="cognitory-provisioning-setup-activation-readiness-loading"
                  style={{ textAlign: "center", color: MAIN_PAGE_COLORS.soft }}
                >
                  Reviewing activation readiness…
                </p>
              ) : null}
              {activationReadinessPhase === "ready" ? (
                <p
                  data-testid="cognitory-provisioning-setup-activation-readiness-ready"
                  style={{ textAlign: "center", color: MAIN_PAGE_COLORS.soft }}
                >
                  Activation readiness verified. Confirm below to activate this
                  Cognitory.
                </p>
              ) : null}
              {activationReadinessPhase === "not_ready" &&
              workflow.activationNotReadyMessage ? (
                <p
                  data-testid="cognitory-provisioning-setup-activation-not-ready"
                  role="alert"
                  style={{
                    textAlign: "center",
                    color: MAIN_PAGE_COLORS.alertInk,
                  }}
                >
                  {workflow.activationNotReadyMessage}
                </p>
              ) : null}
              {workflow.activationErrorMessage ? (
                <p
                  data-testid="cognitory-provisioning-setup-activation-error"
                  role="alert"
                  style={{
                    textAlign: "center",
                    color: MAIN_PAGE_COLORS.alertInk,
                  }}
                >
                  {workflow.activationErrorMessage}
                </p>
              ) : null}
              {activationReadinessPhase === "ready" ? (
                <label
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    alignItems: "flex-start",
                    fontSize: "0.9rem",
                    color: MAIN_PAGE_COLORS.slate,
                    justifyContent: "center",
                    marginTop: "0.75rem",
                    opacity: workflow.activationBusy ? 0.65 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    data-testid="cognitory-provisioning-setup-activation-confirm"
                    checked={confirmActivation}
                    disabled={workflow.activationBusy}
                    onChange={e => setConfirmActivation(e.target.checked)}
                  />
                  <span>
                    I confirm this activates this Cognitory for owner entry. It
                    does not upload documents.
                  </span>
                </label>
              ) : null}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.65rem",
                  justifyContent: "center",
                  marginTop: "0.75rem",
                }}
              >
                {activationReadinessPhase === "ready" ? (
                  <button
                    type="button"
                    data-testid="cognitory-provisioning-setup-activate"
                    disabled={!canSubmitActivation}
                    style={{
                      ...MAIN_PAGE_LIQUID_BUTTON_STYLE,
                      marginTop: 0,
                      opacity: !canSubmitActivation ? 0.65 : 1,
                    }}
                    {...mainPageLiquidButtonHandlers}
                    onClick={() => {
                      if (!canSubmitActivation) {
                        return;
                      }
                      void workflow.executeActivation();
                    }}
                  >
                    {workflow.activationBusy ? "Activating…" : "Activate"}
                  </button>
                ) : null}
                {activationReadinessPhase === "not_ready" ||
                activationReadinessPhase === "unable_to_verify" ||
                activationReadinessPhase === "temporary_failure" ? (
                  <button
                    type="button"
                    data-testid="cognitory-provisioning-setup-activation-retry-readiness"
                    style={MAIN_PAGE_QUIET_BUTTON_STYLE}
                    onClick={() => {
                      workflow.reviewActivationReadiness();
                    }}
                  >
                    Review readiness again
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          <CognitoryProvisioningSetupLibraryPanel
            visible={workflow.uploadEligible}
            uploadBusy={workflow.uploadBusy}
            uploadAccepted={workflow.uploadAccepted}
            uploadActiveFilename={workflow.uploadActiveFilename}
            uploadActiveTitle={workflow.uploadActiveTitle}
            uploadTrackedDocument={workflow.uploadTrackedDocument}
            uploadPollTimedOut={workflow.uploadPollTimedOut}
            uploadErrorMessage={workflow.uploadErrorMessage}
            postUploadLibraryStateRefreshFailed={
              workflow.postUploadLibraryStateRefreshFailed
            }
            onSubmitUpload={uploadInput => {
              void workflow.submitUpload(uploadInput);
            }}
          />

          <p
            data-testid="cognitory-provisioning-setup-next-step"
            style={{
              textAlign: "center",
              color: MAIN_PAGE_COLORS.soft,
              marginBottom: 0,
              lineHeight: 1.6,
            }}
          >
            {nextStepMessage({
              setupPhase: workflow.setupPhase,
              bootstrapAccepted: workflow.bootstrapAccepted,
              bootstrapRefreshFailed: workflow.bootstrapRefreshFailed,
              uploadEligible: workflow.uploadEligible,
              activationReviewVisible: showActivationPanel,
            })}
          </p>
        </div>
      </ProvisioningSetupPageShell>
    );
  }

  const failurePhase = access.phase;
  const showRetry =
    failurePhase === "temporary_failure" || failurePhase === "unable_to_verify";

  return (
    <ProvisioningSetupPageShell>
      <div
        data-testid={`cognitory-provisioning-setup-${failurePhase}`}
        style={MAIN_PAGE_BODY_STYLE}
      >
        <p
          data-testid="cognitory-provisioning-setup-status"
          style={{ textAlign: "center", marginBottom: "0.85rem" }}
        >
          {phaseStatusLabel(failurePhase)}
        </p>
        {access.requestId ? (
          <p
            data-testid="cognitory-provisioning-setup-request-id"
            style={{
              textAlign: "center",
              fontSize: "0.8rem",
              color: MAIN_PAGE_COLORS.whisper,
              marginBottom: "0.65rem",
            }}
          >
            Request ID: {access.requestId}
          </p>
        ) : null}
        {showRetry ? (
          <div style={{ textAlign: "center" }}>
            <button
              type="button"
              data-testid="cognitory-provisioning-setup-retry"
              style={MAIN_PAGE_QUIET_BUTTON_STYLE}
              onClick={access.retry}
            >
              Try again
            </button>
          </div>
        ) : null}
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <a href="/" style={{ color: MAIN_PAGE_COLORS.link }}>
            Return home
          </a>
        </div>
      </div>
    </ProvisioningSetupPageShell>
  );
}
